import { repeat } from "@@/utilities/repeat";
import type { Except } from "type-fest";
import { z } from "zod";
import { assertNever } from "../assert-never";
import { formatBookedTime } from "../date-time/format-booked-time";
import type { IsoAndUnixTimestamp } from "../date-time/iso-and-unix";
import { isIsoAndUnix } from "../date-time/iso-and-unix";
import { tz } from "../date-time/timezone";
import { optionalIdZodSchema } from "../optionals/optional-id";
import {
    type OrderGroupId,
    orderGroupIdZodSchema,
} from "../order-groups/order-group-id";
import type { OrderChangeItem } from "../orders/order-changes/order-change-item";
import {
    type OrderEventId,
    orderEventIdZodSchema,
} from "../orders/order-events/events/_order-event-id";
import type { OrderChange } from "../orders/order-events/events/order-changed-event/order-changes/_order-change";
import type { OrderCreatedEvent } from "../orders/order-events/events/order-created.event";
import { type OrderId, orderIdZodSchema } from "../orders/order-id";
import type { OrderItem } from "../orders/order-items/order-item";
import { orderItemIdZodSchema } from "../orders/order-items/order-item-id";
import {
    getChildAccommodationRates,
    getChildOptionals,
    getChildQuantities,
    isOrderItemWithProduct,
} from "../orders/order-items/order-item-v2-helper";
import {
    type GroupReference,
    groupReferenceZodObject,
} from "../payments/group-reference";
import { negatePrice } from "../prices/negate-price";
import { type Price, priceZodSchema, zeroPrice } from "../prices/price";
import { sumPrices } from "../prices/sum-prices";
import { productIdZodSchema } from "../products/product-id";
import { type ProviderId, providerIdZodSchema } from "../providers/provider-id";
import {
    type Translatable,
    translatableZodSchema,
    translation,
} from "../translations/translation";
import {
    payRequestItemIdFactory,
    payRequestItemIdZodSchema,
} from "./pay-request-item-id";

const payRequestItemZodObject = z.object({
    _id: payRequestItemIdZodSchema,
    _type: z.literal("PAY_REQUEST_ITEM"),
    quantity: z.number().int(),
    type: z.enum(["ADDED", "REMOVED"]),
    group: groupReferenceZodObject.optional(),
    name: translatableZodSchema,
    parentTitle: translatableZodSchema.optional(),
    description: translatableZodSchema.optional(),
    price: priceZodSchema,
    pricePerItem: priceZodSchema,
    providerId: providerIdZodSchema,
    references: z.object({
        productId: productIdZodSchema.nullish(),
        optionalId: optionalIdZodSchema.nullish(),
        orderItemId: orderItemIdZodSchema.nullish(),
        orderId: orderIdZodSchema,
        orderGroupId: orderGroupIdZodSchema,
        providerId: providerIdZodSchema,
        orderEventId: orderEventIdZodSchema,
    }),
});
//

type PayRequestItem = z.infer<typeof payRequestItemZodObject>;
const payRequestItemZodSchema: z.ZodSchema<PayRequestItem> =
    payRequestItemZodObject;

type PayRequestItemFactoryParams = Except<
    PayRequestItem,
    "_id" | "_type" | "price"
>;
const _fromOrderItem = (params: {
    orderItem: OrderItem;
    orderId: OrderId;
    orderGroupId: OrderGroupId;
    orderCreatedEventId: OrderEventId;
    group: GroupReference | IsoAndUnixTimestamp;
    shortDescription?: Translatable;
}): PayRequestItem => {
    // Destructure params
    const { orderItem, orderCreatedEventId, orderId, orderGroupId } = params;
    const group: GroupReference = !isIsoAndUnix(params.group)
        ? params.group
        : {
              key: `order_changed_${params.group.iso}`,
              eventAt: params.group,
              title: translation({
                  sv: "Order",
                  en: "Order",
              }),
          };

    return payRequestItemFactory.from({
        name: orderItem.meta.title,
        type: "ADDED",
        pricePerItem: orderItem.pricePerItem,
        quantity: orderItem.quantity.value,
        providerId: orderItem.providerId,
        parentTitle: params.shortDescription,
        group,
        references: {
            orderId,
            orderGroupId,
            providerId: orderItem.providerId,
            orderEventId: orderCreatedEventId,
            orderItemId: orderItem._id,
            productId: isOrderItemWithProduct(orderItem)
                ? orderItem.productId
                : undefined,
        },
    });
};

const payRequestItemFactory = {
    from: (params: PayRequestItemFactoryParams): PayRequestItem => {
        const item: PayRequestItem = {
            _id: payRequestItemIdFactory(),
            _type: "PAY_REQUEST_ITEM",
            price: sumPrices(repeat(params.pricePerItem, params.quantity)),
            ...params,
        };
        return item;
    },
    fromOrderChange: (
        orderChange: OrderChange,
        orderChangeEventId: OrderEventId,
        group: GroupReference | IsoAndUnixTimestamp,
    ): PayRequestItem => {
        const change = orderChange;
        const _group: GroupReference = !isIsoAndUnix(group)
            ? group
            : {
                  key: `order_changed_${group.iso}`,
                  eventAt: group,
                  title: translation({
                      sv: "Orderändring",
                      en: "Order change",
                  }),
              };
        switch (change.type) {
            case "ORDER_ITEM_ADDED": {
                const orderItem = change.data.orderItem;
                return payRequestItemFactory.from({
                    name: orderItem.meta.title,
                    type: "ADDED",
                    pricePerItem: orderItem.pricePerItem,
                    quantity: orderItem.quantity.value,
                    providerId: change.changeFor.providerId,
                    group: _group,
                    references: {
                        orderId: change.changeFor.orderId,
                        orderGroupId: change.changeFor.orderGroupId,
                        orderEventId: orderChangeEventId,
                        providerId: change.changeFor.providerId,
                        orderItemId: orderItem._id,
                        productId: isOrderItemWithProduct(orderItem)
                            ? orderItem.productId
                            : undefined,
                    },
                });
            }
            case "ORDER_ITEM_REMOVED": {
                const orderItem = change.data.orderItem;
                return payRequestItemFactory.from({
                    name: orderItem.meta.title,
                    type: "REMOVED",
                    pricePerItem: negatePrice(orderItem.pricePerItem),
                    quantity: orderItem.quantity.value,
                    providerId: change.changeFor.providerId,
                    group: _group,
                    references: {
                        orderId: change.changeFor.orderId,
                        orderGroupId: change.changeFor.orderGroupId,
                        orderEventId: orderChangeEventId,
                        providerId: change.changeFor.providerId,
                        orderItemId: orderItem._id,
                        productId: isOrderItemWithProduct(orderItem)
                            ? orderItem.productId
                            : undefined,
                    },
                });
            }
            case "ORDER_ITEM_BOOKABLE_TIME_CHANGED": {
                return payRequestItemFactory.from({
                    name: translation({
                        sv: "Ändrad bokningstid",
                        en: "Changed booking time",
                    }),
                    type: "ADDED",
                    description: translation({
                        sv: `Tid ändrad från ${formatBookedTime({
                            time: change.data.from,
                            currentTimezoneName: tz.EuropeStockholm,
                        })} till ${formatBookedTime({
                            time: change.data.to,
                            currentTimezoneName: tz.EuropeStockholm,
                        })}`,
                    }),
                    pricePerItem: change.price,
                    quantity: 1,
                    providerId: change.changeFor.providerId,
                    group: _group,
                    references: {
                        orderId: change.changeFor.orderId,
                        orderGroupId: change.changeFor.orderGroupId,
                        orderEventId: orderChangeEventId,
                        providerId: change.changeFor.providerId,
                    },
                });
            }
            case "ORDER_ITEM_PRODUCT_CHANGED": {
                return payRequestItemFactory.from({
                    name: translation({
                        sv: "Ändrad produkt",
                        en: "Changed product",
                    }),
                    type: "ADDED",
                    description: translation({
                        sv: `Produkt`,
                    }),
                    pricePerItem: change.price,
                    quantity: 1,
                    providerId: change.changeFor.providerId,
                    group: _group,
                    references: {
                        orderId: change.changeFor.orderId,
                        orderGroupId: change.changeFor.orderGroupId,
                        orderEventId: orderChangeEventId,
                        providerId: change.changeFor.providerId,
                    },
                });
            }
            default:
                assertNever(change, "Unknown change type");
        }
    },
    fromOrderChangeItem: (params: {
        orderChangeItem: OrderChangeItem;
        orderChangeEventId: OrderEventId;
        orderId: OrderId;
        orderGroupId: OrderGroupId;
        providerId: ProviderId;
        group: GroupReference | IsoAndUnixTimestamp;
    }): PayRequestItem => {
        // Destructure params
        const {
            orderChangeItem,
            orderChangeEventId: orderEventId,
            orderId,
            orderGroupId,
            providerId,
        } = params;
        const group: GroupReference = !isIsoAndUnix(params.group)
            ? params.group
            : {
                  key: `order_changed_${params.group.iso}`,
                  eventAt: params.group,
                  title: translation({
                      sv: "Orderändring",
                      en: "Order change",
                  }),
              };
        const changeItem = orderChangeItem;
        switch (changeItem._type) {
            case "ORDER_ITEM_ADD": {
                const orderItem = changeItem.orderItem;
                return payRequestItemFactory.from({
                    name: orderItem.meta.title,
                    type: "ADDED",
                    pricePerItem: orderItem.pricePerItem,
                    quantity: orderItem.quantity.value,
                    providerId,
                    group,
                    references: {
                        orderId,
                        orderGroupId,
                        providerId,
                        orderEventId,
                        orderItemId: orderItem._id,
                        productId: isOrderItemWithProduct(orderItem)
                            ? orderItem.productId
                            : undefined,
                    },
                });
            }
            case "ORDER_ITEM_REMOVE": {
                const orderItem = changeItem.orderItem;
                return payRequestItemFactory.from({
                    name: orderItem.meta.title,
                    type: "REMOVED",
                    pricePerItem: negatePrice(orderItem.pricePerItem),
                    quantity: orderItem.quantity.value,
                    providerId,
                    group,
                    references: {
                        orderId,
                        orderGroupId,
                        providerId,
                        orderEventId,
                        orderItemId: orderItem._id,
                        productId: isOrderItemWithProduct(orderItem)
                            ? orderItem.productId
                            : undefined,
                    },
                });
            }
            case "ORDER_ITEM_BOOKABLE_CHANGE_TIME": {
                return payRequestItemFactory.from({
                    name: translation({
                        sv: "Ändrad bokningstid",
                        en: "Changed booking time",
                    }),
                    type: "ADDED",
                    // according to comment by @jakob in order-change-command-handler.ts:
                    // "There is no change in price for this change type, the price is already included in the ADD/REMOVE change"
                    pricePerItem: zeroPrice,
                    quantity: 1,
                    providerId,
                    group,
                    references: {
                        orderId,
                        orderGroupId,
                        providerId,
                        orderEventId,
                        orderItemId: changeItem.itemId,
                    },
                });
            }
            case "ORDER_ITEM_CHANGE_PRODUCT": {
                //TODO lookover
                return payRequestItemFactory.from({
                    name: translation({
                        sv: "Ändrad produkt",
                        en: "Changed product",
                    }),
                    type: "ADDED",
                    // according to comment by @jakob in order-change-command-handler.ts:
                    // "There is no change in price for this change type, the price is already included in the ADD/REMOVE change"
                    pricePerItem: zeroPrice,
                    quantity: 1,
                    providerId,
                    group,
                    references: {
                        orderId,
                        orderGroupId,
                        providerId,
                        orderEventId,
                        // orderItemId: changeItem.itemId,
                    },
                });
            }
            default:
                assertNever(changeItem, "Unknown change type");
        }
    },

    fromOrderCreated: (params: {
        orderCreatedEvent: OrderCreatedEvent;
    }): PayRequestItem[] => {
        // Destructure params
        const { orderCreatedEvent } = params;
        const { orderGroupId } = orderCreatedEvent.order;
        const orderId = orderCreatedEvent.order._id;
        const group: GroupReference = {
            key: `order_created_${orderCreatedEvent.order._createdAt.iso}`,
            eventAt: params.orderCreatedEvent.order._createdAt,
            title: translation({
                sv: "Order",
                en: "Order",
            }),
        };

        const result: PayRequestItem[] = [];
        const orderItems = orderCreatedEvent.order.orderItems;
        for (const orderItem of orderItems) {
            switch (orderItem.acquireType) {
                case "BOOKABLE": {
                    // If the order item is bookable
                    // We parse the rows to show and returns them
                    // Show a row fot each child with quantity
                    const childrenWithQuantities = getChildQuantities(
                        orderItem,
                        orderItems,
                    );
                    for (const childOrderItemWithQuantity of childrenWithQuantities) {
                        result.push(
                            _fromOrderItem({
                                orderItem: childOrderItemWithQuantity,
                                group,
                                orderCreatedEventId: orderCreatedEvent._id,
                                orderGroupId,
                                orderId,
                                shortDescription: orderItem.meta.title,
                            }),
                        );
                    }
                    // Find all picked old time optionals for this order item
                    const pickedOptionals = getChildOptionals(
                        orderItem,
                        orderItems,
                    );
                    for (const pickedOptional of pickedOptionals) {
                        result.push(
                            _fromOrderItem({
                                orderItem: pickedOptional,
                                group,
                                orderCreatedEventId: orderCreatedEvent._id,
                                orderGroupId,
                                orderId,
                            }),
                        );
                    }
                    continue;
                }
                case "BUYABLE": {
                    const pickedOptionals = getChildOptionals(
                        orderItem,
                        orderItems,
                    );
                    result.push(
                        _fromOrderItem({
                            orderItem,
                            group,
                            orderCreatedEventId: orderCreatedEvent._id,
                            orderGroupId,
                            orderId,
                        }),
                    );
                    for (const pickedOptional of pickedOptionals) {
                        result.push(
                            _fromOrderItem({
                                orderItem: pickedOptional,
                                group,
                                orderCreatedEventId: orderCreatedEvent._id,
                                orderGroupId,
                                orderId,
                            }),
                        );
                    }
                    continue;
                }
                case "VOUCHER": {
                    const pickedOptionals = getChildOptionals(
                        orderItem,
                        orderItems,
                    );
                    result.push(
                        _fromOrderItem({
                            orderItem,
                            group,
                            orderCreatedEventId: orderCreatedEvent._id,
                            orderGroupId,
                            orderId,
                        }),
                    );
                    for (const pickedOptional of pickedOptionals) {
                        result.push(
                            _fromOrderItem({
                                orderItem: pickedOptional,
                                group,
                                orderCreatedEventId: orderCreatedEvent._id,
                                orderGroupId,
                                orderId,
                            }),
                        );
                    }
                    continue;
                }
                case "GIFTCARD":
                case "GIFTCARD_USED":
                case "DISCOUNT": {
                    result.push(
                        _fromOrderItem({
                            orderItem,
                            group,
                            orderCreatedEventId: orderCreatedEvent._id,
                            orderGroupId,
                            orderId,
                        }),
                    );
                    continue;
                }
                case "GENERIC_DISCOUNT":
                case "OPTIONAL":
                case "RESOURCE_REFERENCE":
                case "ACCOMMODATION": {
                    // If the order item is bookable
                    // We parse the rows to show and returns them
                    // Show a row fot each child with quantity
                    const childrenRates = getChildAccommodationRates(
                        orderItem,
                        orderItems,
                    );
                    for (const childOrderItem of childrenRates) {
                        result.push(
                            _fromOrderItem({
                                orderItem: childOrderItem,
                                group,
                                orderCreatedEventId: orderCreatedEvent._id,
                                orderGroupId,
                                orderId,
                                shortDescription: orderItem.meta.title,
                            }),
                        );
                    }
                    // Find all picked old time optionals for this order item
                    const pickedOptionals = getChildOptionals(
                        orderItem,
                        orderItems,
                    );
                    for (const pickedOptional of pickedOptionals) {
                        result.push(
                            _fromOrderItem({
                                orderItem: pickedOptional,
                                group,
                                orderCreatedEventId: orderCreatedEvent._id,
                                orderGroupId,
                                orderId,
                            }),
                        );
                    }
                    continue;
                }
                case "ACCOMMODATION_RATE_ITEM":
                    continue;
                case "GENERIC": {
                    result.push(
                        _fromOrderItem({
                            orderItem: orderItem,
                            group,
                            orderCreatedEventId: orderCreatedEvent._id,
                            orderGroupId,
                            orderId,
                        }),
                    );
                    continue;
                }
                case "QUOTE_HEAD":
                    //TODO IMPLEMENT JAKOB 2025-03-25
                    continue;
                default:
                    assertNever(orderItem, "Unknown order item type");
            }
        }
        return result;
    },
};

const sumPayRequestItemPrices = (items: PayRequestItem[]): Price => {
    return sumPrices(items.map(item => item.price));
};
export {
    payRequestItemFactory,
    payRequestItemZodObject,
    payRequestItemZodSchema,
    sumPayRequestItemPrices,
};
export type { PayRequestItem };
